import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const PRODUCT_POPUP_SECTION_ID = 'efs-product-popup-content';

/**
 * Wraps the trigger button rendered by the `efs-product-popup` block. Fetches
 * the referenced product's popup content via the Section Rendering API and
 * opens the shared `product-popup-dialog` singleton.
 */
export class ProductPopupComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * Handles a click on the popup trigger button.
   * @param {MouseEvent} event - The click event.
   */
  handleClick = async (event) => {
    event.preventDefault();

    // event.currentTarget is document (the declarative on:click listener is attached
    // there, see assets/component.js) — event.target is proxied to the actual [on:click]
    // element by the framework, so it must be used instead.
    const trigger = event.target instanceof HTMLElement ? event.target.closest('[data-product-url]') : null;
    const productUrl = trigger?.dataset.productUrl;
    if (!trigger || !productUrl) return;

    this.#lastTrigger = trigger;

    const sectionHtml = await this.#fetchPopupSection(productUrl);
    if (sectionHtml) {
      this.#updatePopupModal(sectionHtml);
    }

    this.#openPopupModal(trigger);
  };

  /**
   * Fetches the product-popup section content via the Section Rendering API.
   * @param {string} productUrl - The URL of the referenced product.
   * @returns {Promise<string | null>} The section HTML string or null.
   */
  async #fetchPopupSection(productUrl) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      // product.url from Liquid is a relative path (e.g. "/products/handle"),
      // so a base URL is required to construct an absolute URL here.
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('sections', PRODUCT_POPUP_SECTION_ID);

      const response = await fetch(url.toString(), { signal: this.#abortController.signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch product-popup section: HTTP error ${response.status}`);
      }

      const json = await response.json();
      return json[PRODUCT_POPUP_SECTION_ID] || null;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Updates the shared dialog with the fetched section HTML.
   * @param {string} sectionHtml - The section HTML string.
   */
  #updatePopupModal(sectionHtml) {
    const modalContent = document.getElementById('product-popup-modal-content');
    if (!modalContent) return;

    modalContent.innerHTML = sectionHtml;
  }

  /** @param {HTMLElement} trigger */
  #openPopupModal(trigger) {
    const dialogComponent = document.getElementById('product-popup-dialog');
    if (!(dialogComponent instanceof ProductPopupDialog)) return;

    dialogComponent.setPresentation(trigger.dataset.popupStyle, trigger.dataset.drawerSide);

    // Fokus explizit auf den Auslöser-Button zurückgeben, statt dem nativen
    // Focus-Return des <dialog> zu vertrauen (kann auf einem großen
    // umgebenden Container landen und dort einen Fokusrahmen zeichnen).
    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        const trigger = this.#lastTrigger;
        this.#lastTrigger = null;
        if (trigger && trigger.isConnected) {
          trigger.focus({ preventScroll: true });
        }
      },
      { once: true }
    );

    dialogComponent.showDialog();
  }
}

if (!customElements.get('product-popup-component')) {
  customElements.define('product-popup-component', ProductPopupComponent);
}

class ProductPopupDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener('click', this.#handleThumbnailClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Sets the dialog's visual presentation (centered modal vs. left/right drawer).
   * @param {string | undefined} style - 'modal' or 'drawer'.
   * @param {string | undefined} side - 'left' or 'right' (drawer only).
   */
  setPresentation(style, side) {
    const { dialog } = this.refs;
    const isDrawer = style === 'drawer';

    dialog.classList.toggle('dialog-drawer', isDrawer);
    dialog.classList.toggle('product-popup-modal--drawer-right', isDrawer && side === 'right');
    dialog.classList.toggle('product-popup-modal--drawer-left', isDrawer && side !== 'right');
  }

  /**
   * Closes the dialog on successful cart add.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  #handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  /**
   * Updates the main image and product links when the variant changes.
   * @param {CustomEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const variant = event.detail.resource;
    if (!variant) return;

    const img = /** @type {HTMLImageElement | null} */ (this.querySelector('.efs-product-popup-content__main-image img'));
    if (img && variant.featured_image) {
      const newSrc = variant.featured_image.src;
      if (newSrc && img.src !== newSrc) {
        img.src = newSrc;
        img.srcset = '';
        if (variant.featured_image.alt) {
          img.alt = variant.featured_image.alt;
        }
      }
    }

    const variantUrl = variant.url;
    if (variantUrl) {
      for (const link of this.querySelectorAll(
        '.efs-product-popup-content__title-link, .efs-product-popup-content__view-details-link'
      )) {
        if (link instanceof HTMLAnchorElement) {
          link.href = variantUrl;
        }
      }
    }
  };

  /**
   * Swaps the main image when a gallery thumbnail is clicked.
   * @param {MouseEvent} event - The click event.
   */
  #handleThumbnailClick = (event) => {
    const thumbnail = event.target instanceof Element ? event.target.closest('[data-media-id]') : null;
    if (!(thumbnail instanceof HTMLElement)) return;

    const img = /** @type {HTMLImageElement | null} */ (this.querySelector('.efs-product-popup-content__main-image img'));
    const newSrc = thumbnail.dataset.mediaSrc;
    if (!img || !newSrc) return;

    img.src = newSrc;
    img.srcset = '';

    for (const button of this.querySelectorAll('[data-media-id]')) {
      button.classList.toggle('efs-product-popup-content__thumbnail--active', button === thumbnail);
    }
  };
}

if (!customElements.get('product-popup-dialog')) {
  customElements.define('product-popup-dialog', ProductPopupDialog);
}
